import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = "https://365luxuryhomes.com/api/about-page";
const getToken = () => localStorage.getItem("authToken");
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// ─── Reusable Image Upload Component ──────────────────────
const ImageUpload = ({ value, onChange, onUpload, label, aspect = "video", uploading }) => {
  const fileRef = useRef(null);
  const isIcon = aspect === "icon";
  const wrapClass = isIcon ? "w-16 h-16" : "w-full h-28";
  const imgClass = isIcon ? "w-16 h-16 p-1 object-contain" : "w-full h-28 object-cover";

  return (
    <div className="form-control">
      {label && <label className="label py-1"><span className="label-text text-xs font-medium">{label}</span></label>}
      <div
        className={`relative ${wrapClass} border-2 border-dashed border-base-300 rounded-lg overflow-hidden cursor-pointer hover:border-primary/50 transition-colors bg-base-200/50 group`}
        onClick={() => fileRef.current?.click()}
      >
        {value ? (
          <>
            <img src={value} alt="" className={`${imgClass} rounded-lg`} />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-xs font-medium">{uploading ? "Uploading..." : "Change"}</span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={18} height={18} fill="none" className="text-base-content/30">
              <path d="M12 4.5V14.5M12 4.5C11.2998 4.5 9.99153 6.4943 9.5 7M12 4.5C12.7002 4.5 14.0085 6.4943 14.5 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M20 16.5C20 18.982 19.482 19.5 17 19.5H7C4.518 19.5 4 18.982 4 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-[10px] text-base-content/30">Upload</span>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])}
        />
      </div>
      {value && (
        <button
          type="button"
          className="text-[10px] text-error mt-1 hover:underline self-start"
          onClick={(e) => { e.stopPropagation(); onChange(""); }}
        >
          Remove
        </button>
      )}
    </div>
  );
};

const AboutPageManager = () => {
  const [data, setData] = useState({
    welcomeTitle: "Welcome to",
    welcomeDescription: "",
    subsidiaries: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(API);
        if (res.data) setData((prev) => ({ ...prev, ...res.data }));
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, []);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await axios.post(`${API}/upload-image`, fd, {
        headers: authHeaders(),
      });
      return res.data.url;
    } catch (e) {
      alert("Image upload failed");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubChange = (idx, field, value) => {
    setData((prev) => {
      const subs = [...prev.subsidiaries];
      subs[idx] = { ...subs[idx], [field]: value };
      return { ...prev, subsidiaries: subs };
    });
  };

  const handleSubImage = async (idx, file, field) => {
    const url = await uploadImage(file);
    if (url) handleSubChange(idx, field, url);
  };

  const addSubsidiary = () => {
    setData((prev) => ({
      ...prev,
      subsidiaries: [
        ...prev.subsidiaries,
        { name: "", logo: "", description: "", image: "", link: "", buttonText: "Know More" },
      ],
    }));
  };

  const removeSubsidiary = (idx) => {
    setData((prev) => ({
      ...prev,
      subsidiaries: prev.subsidiaries.filter((_, i) => i !== idx),
    }));
  };

  const moveSubsidiary = (idx, dir) => {
    setData((prev) => {
      const subs = [...prev.subsidiaries];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= subs.length) return prev;
      [subs[idx], subs[newIdx]] = [subs[newIdx], subs[idx]];
      return { ...prev, subsidiaries: subs };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(API, data, { headers: authHeaders() });
      alert("About page saved!");
    } catch (e) {
      alert("Failed to save");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-xl font-bold mb-4">About Page Manager</h1>

      <div className="bg-base-100 rounded-lg p-4 lg:p-6 shadow-sm space-y-6">
        {/* Welcome Section */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Welcome Section</h2>
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Title</span>
            </label>
            <input
              className="input input-bordered"
              value={data.welcomeTitle}
              onChange={(e) =>
                setData({ ...data, welcomeTitle: e.target.value })
              }
            />
          </div>
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Description</span>
            </label>
            <textarea
              className="textarea textarea-bordered h-28"
              value={data.welcomeDescription}
              onChange={(e) =>
                setData({ ...data, welcomeDescription: e.target.value })
              }
            />
          </div>
        </div>

        <div className="divider">Subsidiaries</div>

        {/* Subsidiaries */}
        {data.subsidiaries.map((sub, idx) => (
          <div
            key={idx}
            className="card bg-base-200 border border-base-300"
          >
            <div className="card-body p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-sm">
                  Subsidiary #{idx + 1}: {sub.name || "Untitled"}
                </h3>
                <div className="flex gap-1">
                  <button
                    className="btn btn-ghost btn-xs"
                    onClick={() => moveSubsidiary(idx, -1)}
                    disabled={idx === 0}
                  >
                    ↑
                  </button>
                  <button
                    className="btn btn-ghost btn-xs"
                    onClick={() => moveSubsidiary(idx, 1)}
                    disabled={idx === data.subsidiaries.length - 1}
                  >
                    ↓
                  </button>
                  <button
                    className="btn btn-ghost btn-xs text-error"
                    onClick={() => removeSubsidiary(idx)}
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Name</span>
                  </label>
                  <input
                    className="input input-bordered input-sm"
                    value={sub.name}
                    onChange={(e) =>
                      handleSubChange(idx, "name", e.target.value)
                    }
                  />
                </div>
                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">
                      WhatsApp / Link URL
                    </span>
                  </label>
                  <input
                    className="input input-bordered input-sm"
                    value={sub.link}
                    placeholder="https://wa.me/971..."
                    onChange={(e) =>
                      handleSubChange(idx, "link", e.target.value)
                    }
                  />
                </div>
                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text text-xs">Button Text</span>
                  </label>
                  <input
                    className="input input-bordered input-sm"
                    value={sub.buttonText || "Know More"}
                    placeholder="Know More"
                    onChange={(e) =>
                      handleSubChange(idx, "buttonText", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-control">
                <label className="label py-1">
                  <span className="label-text text-xs">Description</span>
                </label>
                <textarea
                  className="textarea textarea-bordered textarea-sm h-24"
                  value={sub.description}
                  onChange={(e) =>
                    handleSubChange(idx, "description", e.target.value)
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ImageUpload
                  value={sub.logo}
                  label="Logo"
                  aspect="icon"
                  uploading={uploading}
                  onChange={(v) => handleSubChange(idx, "logo", v)}
                  onUpload={(f) => handleSubImage(idx, f, "logo")}
                />
                <ImageUpload
                  value={sub.image}
                  label="Section Image"
                  aspect="video"
                  uploading={uploading}
                  onChange={(v) => handleSubChange(idx, "image", v)}
                  onUpload={(f) => handleSubImage(idx, f, "image")}
                />
              </div>
            </div>
          </div>
        ))}

        <div className="flex gap-3">
          <button className="btn btn-outline btn-sm" onClick={addSubsidiary}>
            + Add Subsidiary
          </button>
          <button
            className={`btn btn-primary btn-sm ${saving ? "loading" : ""}`}
            onClick={save}
            disabled={saving || uploading}
          >
            {saving ? "Saving..." : "Save About Page"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutPageManager;
